import type { McpTransport, UploadDocumentMeta, UploadDocumentResult } from "../plugin/transport";
import type { SduiNode } from "../host/declarative/types";

/**
 * Handler for a mocked tool invocation. The handler receives the request
 * payload supplied by the caller and may return synchronously or
 * asynchronously. The result is forwarded verbatim through
 * {@link InMemoryMcpTransport.invokeTool}.
 */
export type MockToolHandler = (args: unknown) => Promise<unknown> | unknown;

/**
 * Handler for a mocked document upload. Receives the {@link UploadDocumentMeta}
 * and the transferred bytes; returns the {@link UploadDocumentResult} the FE
 * hook resolves. Optional — when omitted, the transport returns a synthetic
 * result echoing the metadata so a standalone plugin can exercise the flow.
 */
export type MockUploadHandler = (
    meta: UploadDocumentMeta,
    buffer: ArrayBuffer,
    signal?: AbortSignal,
) => Promise<UploadDocumentResult> | UploadDocumentResult;

/**
 * An in-memory {@link McpTransport} backed by a `{ resources, tools }` map.
 *
 * Used by {@link DeclarativeMockHost} so plugin authors can run their app
 * standalone for local development without a real host. The transport mirrors
 * the runtime contract exactly:
 *
 * - `getResource(uri)` resolves a `SduiNode` keyed by URI, or rejects with a
 *   descriptive error if the URI is not registered.
 * - `invokeTool(name, args)` dispatches to a synchronous or async handler,
 *   or rejects if the tool name is unknown.
 *
 * `getResource` / `invokeTool` intentionally do NOT honour the supplied
 * `AbortSignal` — mock handlers are synchronous from the caller's perspective
 * and there is no in-flight network call to abort. Hooks still work correctly
 * because they treat the `AbortSignal` as a one-way notification, not a
 * contract. `uploadDocument` DOES observe the signal (rejecting with an
 * `AbortError`): its contract mandates it, a mock upload handler may be
 * genuinely async, and dev-host flows need to simulate upload cancellation.
 */
export class InMemoryMcpTransport implements McpTransport
{
    private readonly resources: Record<string, SduiNode>;
    private readonly tools: Record<string, MockToolHandler>;
    private readonly uploadHandler?: MockUploadHandler;

    public constructor(
        resources: Record<string, SduiNode>,
        tools: Record<string, MockToolHandler> = {},
        uploadHandler?: MockUploadHandler,
    )
    {
        this.resources = resources;
        this.tools = tools;
        this.uploadHandler = uploadHandler;
    }

    public async getResource<T>(uri: string): Promise<{ uri: string; data: T }>
    {
        const data = this.resources[uri];
        if (data === undefined)
        {
            throw new Error(`Mock resource not found: ${uri}`);
        }
        return { uri, data: data as unknown as T };
    }

    public async invokeTool<TReq, TRes>(name: string, args: TReq): Promise<TRes>
    {
        const tool = this.tools[name];
        if (!tool)
        {
            throw new Error(`Mock tool not found: ${name}`);
        }
        const result = await tool(args);
        return result as TRes;
    }

    public async uploadDocument(
        meta: UploadDocumentMeta,
        buffer: ArrayBuffer,
        signal?: AbortSignal,
    ): Promise<UploadDocumentResult>
    {
        if (signal?.aborted === true)
        {
            throw abortError();
        }
        const produce = (): Promise<UploadDocumentResult> | UploadDocumentResult =>
        {
            if (this.uploadHandler)
            {
                return this.uploadHandler(meta, buffer, signal);
            }
            // Synthetic default: echo the metadata with a generated id so a
            // standalone plugin can drive the upload flow without a real host.
            return {
                storedDocumentId: `mock-${meta.entityName}-${meta.fileName}`,
                fileName: meta.fileName,
                contentType: meta.contentType,
                sizeBytes: buffer.byteLength,
            };
        };
        if (signal === undefined)
        {
            return produce();
        }
        // Honour cancellation for an async upload handler: reject as soon as the
        // signal fires rather than waiting for the handler to settle.
        return new Promise<UploadDocumentResult>((resolve, reject) =>
        {
            const onAbort = (): void => reject(abortError());
            signal.addEventListener("abort", onAbort, { once: true });
            Promise.resolve(produce()).then(
                (result) =>
                {
                    signal.removeEventListener("abort", onAbort);
                    resolve(result);
                },
                (err: unknown) =>
                {
                    signal.removeEventListener("abort", onAbort);
                    reject(err);
                },
            );
        });
    }
}

/** An `AbortError`-shaped `Error`, matching native fetch cancellation. */
function abortError(): Error
{
    const e = new Error("Aborted");
    e.name = "AbortError";
    return e;
}
